import sx from '../../sx.js';
import { themeGet } from '../../utils/theme-get.js';
import React__default from 'react';
import styled from 'styled-components';
import Box from '../Box/Box.js';
import FuegokitLink from '../Link/Link.js';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden.js';
import Logo from '../Logo/Logo.js';
import Text from '../Text/Text.js';

// --------------------------------------------------------
// Nav base
const Wrapper = styled.li.withConfig({
  displayName: "FooterNav__Wrapper",
  componentId: "sc-1mkgnqw-0"
})(["display:inline-block;white-space:nowrap;list-style:none;&:first-child{margin-left:0;}"]);
const NavBase = styled.nav.withConfig({
  displayName: "FooterNav__NavBase",
  componentId: "sc-1mkgnqw-1"
})(["display:flex;justify-content:space-between;", ""], sx);
const StyledNav = styled(NavBase).withConfig({
  displayName: "FooterNav__StyledNav",
  componentId: "sc-1mkgnqw-2"
})(["display:flex;align-items:center;justify-content:center;padding:0px 8px;margin-left:auto;"]);
const StyledBox = styled(Box).withConfig({
  displayName: "FooterNav__StyledBox",
  componentId: "sc-1mkgnqw-3"
})(["list-style-type:none;padding:0px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:32px;@media (min-width:", "){display:flex;justify-content:flex-end;}@media (min-width:", "){}@media (min-width:", "){}", ""], themeGet('breakpoints.0'), themeGet('breakpoints.1'), themeGet('breakpoints.2'), sx);
/**
 * Every Getting Started page's Footer Nav component renders a semantically accessible `<nav>`
 * that contains an unordered list (`<ul>`) with links that give users actions to take.
 * 
 * Each `FooterNav.Item` is of type `FooterNavAction`, which has the following shape:
 * 
 * export type FooterNavAction = {
  type: 'EXTERNAL' | 'INTERNAL' | 'ADDON'
  url: string
  label: string
  tracking: {
    destination: string
    type: 'button' | 'link' | 'video'
  }
}
 *
 * This makes composing a nav component easy:
 * 
 * <FooterNav>
    <FooterNav.Item {...navItemOne}>{navItemOne.label}</FooterNav.Item>
    <FooterNav.Item {...navItemTwo}>{navItemTwo.label}</FooterNav.Item>
    <FooterNav.Item {...navItemThree}>{navItemThree.label}</FooterNav.Item>
    <FooterNav.Item {...navItemFour}>{navItemFour.label}</FooterNav.Item>
  </FooterNav>
 * 
 * 
 */

const FooterNav = ({
  children,
  sx: sxProp
}) => {
  const wrappedChildren = React__default.Children.map(children, child => /*#__PURE__*/React__default.createElement(Wrapper, null, child));
  return /*#__PURE__*/React__default.createElement(StyledNav, {
    "aria-label": "Footer",
    role: "navigation"
  }, /*#__PURE__*/React__default.createElement(StyledBox, {
    as: "ul",
    sx: sxProp
  }, wrappedChildren));
};
FooterNav.displayName = "FooterNav";
// --------------------------------------------------------
// Nav item
const NavLink = styled(FuegokitLink).withConfig({
  displayName: "FooterNav__NavLink",
  componentId: "sc-1mkgnqw-4"
})(["color:#ffffff;transition:opacity 0.3s ease 0s;opacity:1;padding:0px 8px;-webkit-box-flex:1;flex-grow:1;flex-shrink:1;font-size:", ";overflow:hidden;text-overflow:ellipsis;white-space:nowrap;&:hover,&:focus{background:none;text-decoration:underline;transition-duration:0s,0.15s;color:#ffffff;text-decoration-color:", ";}&:visited{color:#ffffff;}", ""], themeGet('fontSizes.3'), themeGet('colors.border.subtle'), sx);
const FooterNavWrapper = styled(Box).withConfig({
  displayName: "FooterNav__FooterNavWrapper",
  componentId: "sc-1mkgnqw-5"
})(["display:flex;flex-direction:column;flex-wrap:wrap;justify-content:center;align-items:center;margin-top:40px;max-width:972px;@media (min-width:350px){}@media (min-width:", "){}@media (min-width:", "){flex-direction:row;height:64px;width:100%;}@media (min-width:", "){}", ""], themeGet('breakpoints.0'), themeGet('breakpoints.1'), themeGet('breakpoints.2'), sx);
const LogoWrapper = styled.span.withConfig({
  displayName: "FooterNav__LogoWrapper",
  componentId: "sc-1mkgnqw-6"
})(["@media (max-width:", "){padding-bottom:32px;}@media (min-width:", "){padding-bottom:0;margin-right:auto;display:flex;gap:4px;}"], themeGet('breakpoints.1'), themeGet('breakpoints.2'));
// --------------------------------------------------------
// Logo

const FooterNavLogo = () => {
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(LogoWrapper, {
    "data-testid": "appfire-logo"
  }, /*#__PURE__*/React__default.createElement(FuegokitLink, {
    href: "https://www.appfire.com",
    target: "_blank"
  }, /*#__PURE__*/React__default.createElement(VisuallyHidden, null, "Appfire"), /*#__PURE__*/React__default.createElement(Logo, {
    size: 24,
    variant: "animated",
    color: "#fff"
  }))));
};
const CopyrightWrapper = styled.span.withConfig({
  displayName: "FooterNav__CopyrightWrapper",
  componentId: "sc-1mkgnqw-7"
})(["@media (max-width:", "){padding-top:32px;padding-bottom:16px;}@media (min-width:", "){padding-top:0px;margin-top:0px;margin-left:24px;display:flex;gap:4px;}"], themeGet('breakpoints.1'), themeGet('breakpoints.2'));
// --------------------------------------------------------
// Copyright

const Copyright = () => {
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(CopyrightWrapper, {
    "data-testid": "appfire-copy"
  }, /*#__PURE__*/React__default.createElement(Text, {
    as: "p",
    sx: {
      color: '#fff',
      fontSize: '1',
      fontWeight: 'bold'
    }
  }, "\xA9 ", new Date().getFullYear(), " Appfire")));
};

// --------------------------------------------------------
// Exports
FooterNavWrapper.displayName = 'FooterNav.Wrapper';
FooterNav.displayName = 'FooterNav';
NavLink.displayName = 'FooterNav.Item';
Copyright.displayName = 'FooterNav.Copyright';
FooterNavLogo.displayName = 'FooterNav.Logo';
var FooterNav$1 = Object.assign(FooterNav, {
  Wrapper: FooterNavWrapper
}, {
  Item: NavLink
}, {
  Copyright
}, {
  Logo: FooterNavLogo
});

export { FooterNavWrapper, FooterNav$1 as default };
